import { useState } from 'react';
import { Button } from '../../../core/button';
import { cn } from '../../../../utils';
import { ContentDescription } from '../shared/content-description';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentDocumentContent } from '../../hooks/use-class-blocks';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { FilePreviewDialog } from '../shared/file-preview-dialog';

interface DocumentContentDisplayProps {
  content: StudentDocumentContent;
  variant?: ContentDisplayVariant;
}

export function DocumentContentDisplay({ content, variant = 'card' }: DocumentContentDisplayProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  if (!content.url) {
    return null;
  }

  return (
    <div
      className={cn(
        'space-y-2',
        variant === 'card' && 'rounded-lg border border-border bg-card p-4 shadow-sm'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          {content.title && (
            <h3 className="text-lg font-semibold">
              {renderInlineMarkdown(content.title, 'document-content-title')}
            </h3>
          )}
          <ContentDescription text={content.description} markdown />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setIsPreviewOpen(true)}>
            View Document
          </Button>
          <Button asChild variant="ghost" size="sm">
            <a href={content.url} target="_blank" rel="noopener noreferrer">
              Download
            </a>
          </Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        {content.isPdf && 'PDF document'}
        {content.isWord && !content.isPdf && 'Word document'}
      </div>
      <FilePreviewDialog
        file={
          isPreviewOpen
            ? {
                url: content.url,
                filename: content.title ?? undefined,
                contentType: content.isPdf
                  ? 'application/pdf'
                  : content.isWord
                    ? 'application/msword'
                    : undefined,
              }
            : null
        }
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
}
